import React, { useMemo } from "react";
import {
  CalendarDays,
  Clock,
  Code2,
  RefreshCw,
  Repeat,
  Sunrise,
  Timer,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────────
 * CronEditor — a friendly visual editor for 5-field cron expressions.
 *
 * The end user picks a preset card (Every N minutes / Hourly / Every N hours /
 * Daily / Weekly) and adjusts a couple of inputs. The component computes the
 * raw cron expression and emits it via `onChange`. An "Advanced" preset
 * exposes the raw text input as an escape hatch for power users.
 *
 * The component is fully controlled — the parent owns `value` and re-renders
 * when `onChange` fires.
 * ────────────────────────────────────────────────────────────────────────── */

export type CronPreset =
  | "every-n-min"
  | "hourly"
  | "every-n-hours"
  | "daily"
  | "weekly"
  | "custom";

export interface ParsedCron {
  preset: CronPreset;
  minute: number; // 0-59
  hour: number; // 0-23
  dayOfWeek: number; // 0-6 (Sun..Sat)
  everyMinutes: number;
  everyHours: number;
  custom: string;
}

const DEFAULTS: ParsedCron = {
  preset: "hourly",
  minute: 0,
  hour: 9,
  dayOfWeek: 1,
  everyMinutes: 15,
  everyHours: 6,
  custom: "0 * * * *",
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const N_MIN_OPTIONS = [5, 10, 15, 20, 30];
const N_HOUR_OPTIONS = [2, 3, 4, 6, 8, 12];
const HOURLY_MINUTE_OPTIONS = [0, 5, 10, 15, 20, 30, 45];

/* ── Parsing / Building ─────────────────────────────────────────────────── */

export function parseCron(expr: string): ParsedCron {
  const def: ParsedCron = { ...DEFAULTS, custom: (expr || "").trim() };
  const parts = (expr || "").trim().split(/\s+/);
  if (parts.length !== 5) return def;
  const [m, h, dom, mon, dow] = parts;

  // every-n-min:  */N * * * *
  if (h === "*" && dom === "*" && mon === "*" && dow === "*" && /^\*\/\d+$/.test(m)) {
    const n = parseInt(m.slice(2), 10);
    if (n > 0) return { ...def, preset: "every-n-min", everyMinutes: n };
  }

  // hourly:  M * * * *
  if (h === "*" && dom === "*" && mon === "*" && dow === "*" && /^\d+$/.test(m)) {
    return { ...def, preset: "hourly", minute: parseInt(m, 10) };
  }

  // every-n-hours:  0 */N * * *
  if (m === "0" && dom === "*" && mon === "*" && dow === "*" && /^\*\/\d+$/.test(h)) {
    const n = parseInt(h.slice(2), 10);
    if (n > 0) return { ...def, preset: "every-n-hours", everyHours: n };
  }

  // daily:  M H * * *
  if (dom === "*" && mon === "*" && dow === "*" && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return { ...def, preset: "daily", minute: parseInt(m, 10), hour: parseInt(h, 10) };
  }

  // weekly:  M H * * D
  if (
    dom === "*" &&
    mon === "*" &&
    /^\d+$/.test(m) &&
    /^\d+$/.test(h) &&
    /^\d+$/.test(dow)
  ) {
    return {
      ...def,
      preset: "weekly",
      minute: parseInt(m, 10),
      hour: parseInt(h, 10),
      dayOfWeek: parseInt(dow, 10),
    };
  }

  return { ...def, preset: "custom" };
}

export function buildCron(p: ParsedCron): string {
  const m = clamp(p.minute, 0, 59);
  const h = clamp(p.hour, 0, 23);
  const d = clamp(p.dayOfWeek, 0, 6);
  switch (p.preset) {
    case "every-n-min":
      return `*/${clamp(p.everyMinutes, 1, 59)} * * * *`;
    case "hourly":
      return `${m} * * * *`;
    case "every-n-hours":
      return `0 */${clamp(p.everyHours, 1, 23)} * * *`;
    case "daily":
      return `${m} ${h} * * *`;
    case "weekly":
      return `${m} ${h} * * ${d}`;
    case "custom":
      return p.custom;
  }
}

export function describePreset(p: ParsedCron): string {
  const t = (h: number, m: number) =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  switch (p.preset) {
    case "every-n-min":
      return `Every ${p.everyMinutes} minutes`;
    case "hourly":
      return p.minute === 0
        ? "Every hour at :00"
        : `Every hour at :${String(p.minute).padStart(2, "0")}`;
    case "every-n-hours":
      return `Every ${p.everyHours} hours (at :00)`;
    case "daily":
      return `Daily at ${t(p.hour, p.minute)} UTC`;
    case "weekly":
      return `Every ${DAYS[p.dayOfWeek]} at ${t(p.hour, p.minute)} UTC`;
    case "custom":
      return "Custom expression";
  }
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/* ── Public component ───────────────────────────────────────────────────── */

interface Props {
  value: string;
  onChange: (cron: string) => void;
  defaultCron?: string;
}

export const CronEditor: React.FC<Props> = ({ value, onChange, defaultCron }) => {
  // Re-derive parsed state from the controlled `value` so external resets
  // (Reset to default button) are reflected immediately.
  const parsed = useMemo(() => parseCron(value), [value]);

  const update = (patch: Partial<ParsedCron>) => {
    const next = { ...parsed, ...patch };
    onChange(buildCron(next));
  };

  const setPreset = (preset: CronPreset) => {
    if (preset === parsed.preset) return;
    if (preset === "custom") {
      update({ preset, custom: value });
    } else {
      update({ preset });
    }
  };

  const time = `${String(parsed.hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")}`;

  return (
    <div>
      {/* Preset cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
          marginBottom: 18,
        }}
      >
        <PresetCard
          icon={<Repeat size={16} />}
          label="Every N minutes"
          hint="like every 15 min"
          active={parsed.preset === "every-n-min"}
          onClick={() => setPreset("every-n-min")}
        />
        <PresetCard
          icon={<Clock size={16} />}
          label="Hourly"
          hint="at a chosen :MM"
          active={parsed.preset === "hourly"}
          onClick={() => setPreset("hourly")}
        />
        <PresetCard
          icon={<Timer size={16} />}
          label="Every N hours"
          hint="like every 6 h"
          active={parsed.preset === "every-n-hours"}
          onClick={() => setPreset("every-n-hours")}
        />
        <PresetCard
          icon={<Sunrise size={16} />}
          label="Daily"
          hint="once a day at HH:MM"
          active={parsed.preset === "daily"}
          onClick={() => setPreset("daily")}
        />
        <PresetCard
          icon={<CalendarDays size={16} />}
          label="Weekly"
          hint="one day a week"
          active={parsed.preset === "weekly"}
          onClick={() => setPreset("weekly")}
        />
        <PresetCard
          icon={<Code2 size={16} />}
          label="Advanced"
          hint="raw cron expression"
          active={parsed.preset === "custom"}
          onClick={() => setPreset("custom")}
        />
      </div>

      {/* Inputs per preset */}
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          borderRadius: 12,
          padding: 16,
          marginBottom: 12,
        }}
      >
        {parsed.preset === "every-n-min" && (
          <Row label="Run every">
            <NumberPicker
              options={N_MIN_OPTIONS}
              value={parsed.everyMinutes}
              suffix="minutes"
              onChange={(v) => update({ everyMinutes: v })}
            />
          </Row>
        )}

        {parsed.preset === "hourly" && (
          <Row label="At minute">
            <NumberPicker
              options={HOURLY_MINUTE_OPTIONS}
              value={parsed.minute}
              suffix="of every hour"
              format={(n) => `:${String(n).padStart(2, "0")}`}
              onChange={(v) => update({ minute: v })}
            />
          </Row>
        )}

        {parsed.preset === "every-n-hours" && (
          <Row label="Run every">
            <NumberPicker
              options={N_HOUR_OPTIONS}
              value={parsed.everyHours}
              suffix="hours (at :00)"
              onChange={(v) => update({ everyHours: v })}
            />
          </Row>
        )}

        {(parsed.preset === "daily" || parsed.preset === "weekly") && (
          <>
            {parsed.preset === "weekly" && (
              <Row label="Day of week">
                <DayOfWeekPicker
                  value={parsed.dayOfWeek}
                  onChange={(v) => update({ dayOfWeek: v })}
                />
              </Row>
            )}
            <Row label="Time (UTC)">
              <input
                type="time"
                value={time}
                onChange={(e) => {
                  const [hh, mm] = e.target.value.split(":");
                  update({ hour: parseInt(hh || "0", 10), minute: parseInt(mm || "0", 10) });
                }}
                style={{
                  padding: "8px 12px",
                  border: "1.5px solid var(--border-default)",
                  borderRadius: 8,
                  fontSize: 15,
                  fontFamily: "ui-monospace, monospace",
                  background: "var(--surface-page)",
                  width: 130,
                }}
              />
              <span style={{ color: "var(--text-faint)", fontSize: 12, marginLeft: 8 }}>
                24-hour clock, UTC. Don't forget to convert from local time.
              </span>
            </Row>
          </>
        )}

        {parsed.preset === "custom" && (
          <Row label="Cron expression">
            <input
              value={parsed.custom}
              onChange={(e) => update({ custom: e.target.value })}
              placeholder="0 * * * *"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1.5px solid var(--border-default)",
                borderRadius: 8,
                fontSize: 14,
                fontFamily: "ui-monospace, monospace",
                background: "var(--surface-inverse)",
                color: "#e2e8f0",
              }}
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--text-faint)",
                marginTop: 6,
                fontFamily: "ui-monospace, monospace",
              }}
            >
              fields: minute&nbsp; hour&nbsp; day-of-month&nbsp; month&nbsp; day-of-week
            </div>
          </Row>
        )}
      </div>

      {/* Result preview */}
      <div
        style={{
          background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
          border: "1px solid var(--brand-primary-subtle)",
          borderRadius: 12,
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 9,
            background: "var(--brand-primary)",
            color: "var(--text-on-brand)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Clock size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--status-info-text)" }}>
            {describePreset(parsed)}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--brand-primary-active)",
              marginTop: 3,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {value || "(empty)"}
          </div>
        </div>
        {defaultCron && defaultCron !== value && (
          <button
            type="button"
            onClick={() => onChange(defaultCron)}
            title="Reset to the job's default schedule"
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 10px",
              border: "1px solid var(--brand-primary-subtle)",
              background: "var(--surface-card)",
              color: "var(--brand-primary)",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <RefreshCw size={11} /> Reset
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Sub-components ──────────────────────────────────────────────────────── */

const PresetCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, hint, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      textAlign: "left",
      padding: "12px 14px",
      borderRadius: 12,
      border: active ? "2px solid #4f46e5" : "1px solid #e5e7eb",
      background: active
        ? "linear-gradient(135deg, #eef2ff 0%, #ede9fe 100%)"
        : "#fff",
      cursor: "pointer",
      transition: "all 120ms",
      boxShadow: active ? "0 4px 12px rgba(79, 70, 229, 0.18)" : "none",
    }}
    onMouseEnter={(e) => {
      if (!active) {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "#a5b4fc";
        (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-page)";
      }
    }}
    onMouseLeave={(e) => {
      if (!active) {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--border-default)";
        (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-card)";
      }
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: active ? "#4f46e5" : "#475569",
        fontWeight: 700,
        fontSize: 13,
        marginBottom: 3,
      }}
    >
      {icon} {label}
    </div>
    <div
      style={{
        fontSize: 11,
        color: active ? "#4f46e5" : "#94a3b8",
        fontWeight: 500,
      }}
    >
      {hint}
    </div>
  </button>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div style={{ marginBottom: 14 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "var(--text-body)",
        textTransform: "uppercase",
        letterSpacing: 0.6,
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
      {children}
    </div>
  </div>
);

const NumberPicker: React.FC<{
  options: number[];
  value: number;
  suffix?: string;
  format?: (n: number) => string;
  onChange: (n: number) => void;
}> = ({ options, value, suffix, format, onChange }) => (
  <>
    <div
      style={{
        display: "inline-flex",
        background: "var(--surface-sunken)",
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((n) => {
        const active = n === value;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            style={{
              padding: "6px 14px",
              borderRadius: 8,
              border: "none",
              background: active ? "#fff" : "transparent",
              color: active ? "#0f172a" : "#64748b",
              fontWeight: active ? 700 : 500,
              fontSize: 13,
              cursor: "pointer",
              boxShadow: active ? "0 1px 2px rgba(15,23,42,0.1)" : "none",
              minWidth: 44,
              fontFamily: "ui-monospace, monospace",
            }}
          >
            {format ? format(n) : n}
          </button>
        );
      })}
    </div>
    {suffix && (
      <span style={{ marginLeft: 12, color: "var(--text-body)", fontSize: 13 }}>{suffix}</span>
    )}
  </>
);

const DayOfWeekPicker: React.FC<{
  value: number;
  onChange: (n: number) => void;
}> = ({ value, onChange }) => (
  <div
    style={{
      display: "inline-flex",
      background: "var(--surface-sunken)",
      borderRadius: 10,
      padding: 3,
      gap: 2,
    }}
  >
    {DAYS.map((d, i) => {
      const active = i === value;
      return (
        <button
          key={d}
          type="button"
          onClick={() => onChange(i)}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "none",
            background: active ? "#fff" : "transparent",
            color: active ? "#0f172a" : "#64748b",
            fontWeight: active ? 700 : 500,
            fontSize: 13,
            cursor: "pointer",
            boxShadow: active ? "0 1px 2px rgba(15,23,42,0.1)" : "none",
            minWidth: 44,
          }}
        >
          {d}
        </button>
      );
    })}
  </div>
);

export default CronEditor;
